import { useEffect, useRef } from 'react'

interface UseModalA11yOptions<D extends HTMLElement, F extends HTMLElement> {
  /** Whether the modal is currently open. Components in this codebase stay
   *  mounted and render `null` while closed (see InviteModal.tsx), so the
   *  hook reacts to this flag rather than to mount/unmount. */
  active: boolean
  /** Ref to the dialog panel element that bounds the Tab focus trap. */
  dialogRef: React.RefObject<D | null>
  /** Ref to the element that receives focus when the modal opens. */
  firstFocusRef: React.RefObject<F | null>
  onClose: () => void
}

/**
 * Shared accessibility plumbing for modal dialogs (WCAG 2.1 AA):
 * - Autofocus the first interactive element whenever `active` becomes true.
 * - ESC closes the modal.
 * - Tab / Shift+Tab traps focus inside `dialogRef`.
 * - When `active` becomes false (or on unmount), focus returns to whatever
 *   had focus before the modal opened (e.g. the button that triggered it).
 *
 * Extracted from the manual pattern in `SettingsModal.tsx` so new modals
 * (ChangeRoleModal, CreateRoleModal — P0-5) don't duplicate it. Existing
 * modals are intentionally left untouched (follow-up, not this PR's scope).
 */
export function useModalA11y<D extends HTMLElement, F extends HTMLElement>({
  active,
  dialogRef,
  firstFocusRef,
  onClose,
}: UseModalA11yOptions<D, F>): void {
  const previouslyFocused = useRef<HTMLElement | null>(null)

  // Autofocus when opening + restore focus to the opener when closing.
  useEffect(() => {
    if (!active) return
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    firstFocusRef.current?.focus()

    return () => {
      previouslyFocused.current?.focus()
    }
  }, [active, firstFocusRef])

  // ESC close + Tab/Shift+Tab focus trap, only while open.
  useEffect(() => {
    if (!active) return

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab') return

      const dialog = dialogRef.current
      if (!dialog) return
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [active, dialogRef, onClose])
}
